/**
 * OAuth Callback Page
 * P4 - Handles OAuth redirect with tokens
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { rhieEventBus, RHIE_EVENTS } from '../services/zihinKilavuzuEventBus';

export default function OAuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [message, setMessage] = useState('Giriş yapılıyor...');
  
  useEffect(() => {
    processOAuthCallback();
  }, []);
  
  const processOAuthCallback = async () => {
    try {
      // Get tokens from URL
      const accessToken = searchParams.get('access_token');
      const refreshToken = searchParams.get('refresh_token');
      const isNewUser = searchParams.get('is_new_user') === 'true';
      const error = searchParams.get('oauth_error');
      const errorMessage = searchParams.get('message');
      
      // Check for errors
      if (error) {
        setStatus('error');
        setMessage(errorMessage || 'OAuth girişi başarısız');
        rhieEventBus.emit(RHIE_EVENTS.OAUTH_LOGIN_FAILED, { error });
        
        setTimeout(() => {
          navigate('/giris', { state: { error: errorMessage || 'Giriş başarısız' } });
        }, 2000);
        return;
      }
      
      // Check for tokens
      if (!accessToken) {
        setStatus('error');
        setMessage('Token bulunamadı');
        
        setTimeout(() => {
          navigate('/giris', { state: { error: 'Token bulunamadı' } });
        }, 2000);
        return;
      }
      
      // Save token and fetch user
      localStorage.setItem('token', accessToken);
      if (refreshToken) {
        localStorage.setItem('refresh_token', refreshToken);
      }
      
      // Emit success event
      rhieEventBus.emit(RHIE_EVENTS.OAUTH_LOGIN_SUCCESS, { 
        isNewUser,
        provider: 'oauth' 
      });
      
      setStatus('success');
      setMessage(isNewUser ? 'Hoş geldin!' : 'Giriş başarılı!');
      
      // FAZ 7: Redirect to Ritual Entry first
      setTimeout(() => {
        navigate('/panel/entry', { replace: true });
        window.location.reload(); // Refresh to load user data
      }, 1500);
      
    } catch (error) {
      console.error('OAuth callback error:', error);
      setStatus('error');
      setMessage('Bir hata oluştu');
      
      setTimeout(() => {
        navigate('/giris', { state: { error: 'Giriş başarısız' } });
      }, 2000);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
      <div className="text-center">
        {status === 'processing' && (
          <>
            <Loader2 className="w-12 h-12 text-purple-400 animate-spin mx-auto mb-4" />
            <p className="text-white/70 text-lg">{message}</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <p className="text-white text-lg font-medium">{message}</p>
            <p className="text-white/50 text-sm mt-2">Yönlendiriliyorsunuz...</p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-white text-lg font-medium">{message}</p>
            <p className="text-white/50 text-sm mt-2">Giriş sayfasına yönlendiriliyorsunuz...</p>
          </>
        )}
      </div>
    </div>
  );
}
